import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FlaskConical, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function BetaAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/beta/${token}`).then((r) => {
      setInfo(r.data);
      setName(r.data.name || "");
      setState(r.data.status === "revoked" || r.data.status === "expired" ? "invalid" : "ready");
    }).catch(() => setState("invalid"));
  }, [token]);

  const accept = async () => {
    if (password.length < 6) return toast.error("Choose a password (6+ characters)");
    setBusy(true);
    try {
      await api.post(`/beta/${token}/accept`, { password, name });
      if (refreshUser) await refreshUser();
      toast.success("Welcome to the beta — 30-day trial started!");
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070d1c] text-slate-100 flex items-center justify-center p-4" data-testid="beta-accept-page">
      <div className="w-full max-w-md bg-[#0d1730] border border-emerald-500/30 rounded-2xl p-7">
        {state === "loading" && <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>}
        {state === "invalid" && (
          <div className="py-8 text-center" data-testid="beta-invalid">
            <AlertCircle className="w-10 h-10 text-rose-400 mx-auto mb-3" />
            <p className="text-lg font-semibold">This beta invite isn't valid</p>
            <p className="text-sm text-slate-400 mt-2">It may have been used, revoked, or expired.</p>
          </div>
        )}
        {state === "ready" && info && (
          <>
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 px-2.5 py-1 rounded-full"><FlaskConical className="w-3.5 h-3.5" /> Beta invitation</span>
            <h1 className="text-2xl font-bold mt-4">You're invited, {info.name || "friend"}!</h1>
            <p className="text-sm text-slate-400 mt-2">Activate your <b className="text-emerald-300">{info.trial_days}-day full-access beta trial</b>. During beta, all reminders are email-only and voice/text features run in preview mode.</p>
            <div className="mt-6 space-y-3">
              <div>
                <label className="text-xs text-slate-400">Your name</label>
                <input data-testid="beta-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-lg bg-[#0a1224] border border-white/10 outline-none focus:border-emerald-500/50 text-sm" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Email</label>
                <input value={info.email} disabled className="mt-1 w-full h-11 px-3 rounded-lg bg-[#0a1224] border border-white/10 text-slate-500 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Create a password</label>
                <input data-testid="beta-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6+ characters" className="mt-1 w-full h-11 px-3 rounded-lg bg-[#0a1224] border border-white/10 outline-none focus:border-emerald-500/50 text-sm" />
              </div>
            </div>
            <button data-testid="beta-activate" onClick={accept} disabled={busy} className="w-full mt-6 h-12 rounded-xl bg-emerald-500 text-[#04140c] font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Start my beta trial</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
